import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { InboxProvider } from './context/InboxContext';
import { ProtectedRoute } from './auth/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './auth/Login';
import ResetPassword from './auth/ResetPassword';
import Callback from './auth/Callback';
import ResponsiveNavbar from './components/ResponsiveNavbar';
import TopNavigation from './components/TopNavigation';
import MobileBottomNav from './components/MobileBottomNav';
import SessionTimeoutDialog from './components/SessionTimeoutDialog';

// Lazy load page components for code splitting

const Dashboard = lazy(() => import('./pages/Dashboard'));
const LeaveManagementDashboard = lazy(() => import('./pages/LeaveManagement/Dashboard'));
const LeaveApplyEnhanced = lazy(() => import('./pages/LeaveManagement/LeaveApplyEnhanced'));
const MyLeaves = lazy(() => import('./pages/LeaveManagement/MyLeaves'));
const AddAsset = lazy(() => import('./pages/AssetManagement/Addasset'));
const AssignAsset = lazy(() => import('./pages/AssetManagement/assignasset'));
const ViewAssets = lazy(() => import('./pages/AssetManagement/ViewAssets'));
const MyAssets = lazy(() => import('./pages/AssetManagement/MyAssets'));
const EmployeeList = lazy(() => import('./pages/HRManagement/EmployeeList'));
const EmployeeDetails = lazy(() => import('./pages/HRManagement/EmployeeDetails'));
const ResourceDirectory = lazy(() => import('./pages/ResourceDirectory/ResourceDirectory'));
const SubmitClaim = lazy(() => import('./pages/ExpenseClaims/SubmitClaim'));
const ExpenseClaimsDashboard = lazy(() => import('./pages/ExpenseClaims/Dashboard'));
const ApproveClaims = lazy(() => import('./pages/ExpenseClaims/ApproveClaims'));
const MyClaims = lazy(() => import('./pages/ExpenseClaims/MyClaims'));

const MyForms = lazy(() => import('./pages/FormManagement/MyForms'));
const ManageForms = lazy(() => import('./pages/FormManagement/ManageForms'));
const CreateForm = lazy(() => import('./pages/FormManagement/CreateForm'));
const PreviewForm = lazy(() => import('./pages/FormManagement/PreviewForm'));
const FormResponses = lazy(() => import('./pages/FormManagement/FormResponses'));
const CreateCertificate = lazy(() => import('./pages/FormManagement/CreateCertificate'));
const HolidayCalendar = lazy(() => import('./pages/HolidayManagement/HolidayCalendar'));
const ManageHolidays = lazy(() => import('./pages/HolidayManagement/ManageHolidays'));
const MyInbox = lazy(() => import('./pages/MyInbox/MyInbox'));
const RaiseTicket = lazy(() => import('./pages/TicketManagement/RaiseTicket'));
const ApproveTickets = lazy(() => import('./pages/TicketManagement/ApproveTickets'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const Settings = lazy(() => import('./pages/Settings'));
const NotFound = lazy(() => import('./pages/Errors/NotFound'));
const Unauthorized = lazy(() => import('./pages/Errors/Unauthorized'));
const UnderDevelopment = lazy(() => import('./pages/Errors/UnderDevelopment'));

// Loading fallback component
const PageLoader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
    <CircularProgress />
  </Box>
);


const AppContent: React.FC = () => {
  const { isAuthenticated, loading, showSessionWarning, remainingSessionTime, logout, extendSession } = useAuth();

  const handleLogout = async () => {
    await logout();
    // Use window.location.href to ensure full page reload and proper redirect
    window.location.href = '/login';
  };

  // 🔴 VERY IMPORTANT: wait for auth hydration
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/callback" element={<Callback />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/under-development" element={<UnderDevelopment />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    );
  }

  return (
    <InboxProvider>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <SessionTimeoutDialog
          open={showSessionWarning}
          remainingTime={remainingSessionTime}
          onExtend={extendSession}
          onLogout={handleLogout}
        />
        <ResponsiveNavbar />
        <TopNavigation />
        <MobileBottomNav />
        <Box
          component="main"
          sx={{
            flex: 1,
            overflow: 'auto',
            px: { xs: 1, sm: 2, md: 3 },
            py: { xs: 2, sm: 2 },
            pt: 0,
            pb: { xs: '80px', sm: 2 },
            '@media (max-width: 640px)': {
              px: 1,
              py: 1,
              pt: 0
            }
          }}
        >

          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />

              <Route
                path="/leaves/dashboard"
                element={<ProtectedRoute><LeaveManagementDashboard /></ProtectedRoute>}
              />
              <Route
                path="/leaves/apply"
                element={<ProtectedRoute requiredRoles={['user', 'manager', 'admin']}><LeaveApplyEnhanced /></ProtectedRoute>}
              />
              <Route
                path="/leaves/my-leaves"
                element={<ProtectedRoute requiredRoles={['user', 'manager', 'admin']}><MyLeaves /></ProtectedRoute>}
              />
              <Route
                path="/leaves/approve"
                element={<ProtectedRoute requiredRoles={['admin', 'manager']}><Navigate to="/inbox" replace /></ProtectedRoute>}
              />

              <Route
                path="/assets/view"
                element={<ProtectedRoute><ViewAssets /></ProtectedRoute>}
              />
              <Route
                path="/assets/add"
                element={<ProtectedRoute requiredRoles={['admin']}><AddAsset /></ProtectedRoute>}
              />
              <Route
                path="/assets/assign"
                element={<ProtectedRoute requiredRoles={['admin']}><AssignAsset /></ProtectedRoute>}
              />
              <Route
                path="/assets/my-assets"
                element={<ProtectedRoute><MyAssets /></ProtectedRoute>}
              />

              <Route
                path="/hr/employees"
                element={<ProtectedRoute requiredRoles={['admin']}><EmployeeList /></ProtectedRoute>}
              />
              <Route
                path="/hr/details/:id"
                element={<ProtectedRoute requiredRoles={['admin']}><EmployeeDetails /></ProtectedRoute>}
              />

              <Route
                path="/resources/directory"
                element={<ProtectedRoute requiredRoles={['admin', 'manager']}><ResourceDirectory /></ProtectedRoute>}
              />
              <Route
                path="/resources/add"
                element={<ProtectedRoute requiredRoles={['admin']}><ResourceDirectory /></ProtectedRoute>}
              />

              <Route
                path="/expenses/dashboard"
                element={<ProtectedRoute><ExpenseClaimsDashboard /></ProtectedRoute>}
              />
              <Route
                path="/expenses/submit"
                element={<ProtectedRoute requiredRoles={['user', 'manager', 'admin']}><SubmitClaim /></ProtectedRoute>}
              />
              <Route
                path="/expenses/my-claims"
                element={<ProtectedRoute requiredRoles={['user', 'manager', 'admin']}><MyClaims /></ProtectedRoute>}
              />
              <Route
                path="/expenses/approve"
                element={<ProtectedRoute requiredRoles={['admin', 'manager']}><ApproveClaims /></ProtectedRoute>}
              />


              <Route
                path="/forms/my-forms"
                element={<ProtectedRoute requiredRoles={['user', 'admin']}><MyForms /></ProtectedRoute>}
              />
              <Route
                path="/forms/create"
                element={<ProtectedRoute requiredRoles={['user', 'admin']}><CreateForm /></ProtectedRoute>}
              />
              <Route
                path="/forms/preview/:formId"
                element={<ProtectedRoute requiredRoles={['user', 'admin']}><PreviewForm /></ProtectedRoute>}
              />
              <Route
                path="/forms/:formId/edit"
                element={<ProtectedRoute requiredRoles={['user', 'admin']}><CreateForm /></ProtectedRoute>}
              />
              <Route
                path="/forms/manage"
                element={<ProtectedRoute requiredRoles={['admin']}><ManageForms /></ProtectedRoute>}
              />
              <Route
                path="/forms/responses"
                element={<ProtectedRoute requiredRoles={['admin']}><FormResponses /></ProtectedRoute>}
              />
              <Route
                path="/forms/responses/:formName"
                element={<ProtectedRoute requiredRoles={['admin']}><FormResponses /></ProtectedRoute>}
              />
              <Route
                path="/forms/create-certificate"
                element={<ProtectedRoute requiredRoles={['admin']}><CreateCertificate /></ProtectedRoute>}
              />

              <Route
                path="/holidays/calendar"
                element={<ProtectedRoute><HolidayCalendar /></ProtectedRoute>}
              />
              <Route
                path="/holidays/manage"
                element={<ProtectedRoute requiredRoles={['admin']}><ManageHolidays /></ProtectedRoute>}
              />

              <Route
                path="/inbox"
                element={<ProtectedRoute><MyInbox /></ProtectedRoute>}
              />
              <Route
                path="/tickets/raise"
                element={<ProtectedRoute><RaiseTicket /></ProtectedRoute>}
              />
              <Route
                path="/tickets/my-tickets"
                element={<ProtectedRoute><MyInbox /></ProtectedRoute>}
              />
              <Route
                path="/tickets/approve"
                element={<ProtectedRoute><ApproveTickets /></ProtectedRoute>}
              />

              <Route
                path="/profile"
                element={<ProtectedRoute><UserProfile /></ProtectedRoute>}
              />
              <Route
                path="/settings"
                element={<ProtectedRoute><Settings /></ProtectedRoute>}
              />

              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="/under-development" element={<UnderDevelopment />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </Box>
      </Box>

    </InboxProvider>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ErrorBoundary>
          <AppContent />
        </ErrorBoundary>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
